/**
 * The rate card — the one place every commercial number lives.
 *
 * A published card is never edited. Changing a rate publishes a NEW version, which applies to
 * partners who sign after it; everyone already earning keeps the card they signed under. That
 * is why this screen has no "save" on an existing row — only "publish a new version" — and why
 * it shows how many partners are pinned to each one.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Receipt, Plus, Lock } from 'lucide-react';

// The commission table is keyed by the CLIENT's arrangement, not by a kind of partner —
// `reseller` is absent because that client pays no commission at all: the partner already
// took their margin as a wallet discount.
const ARRANGEMENTS = ['solution', 'referral', 'affiliate'];
const TIERS = ['silver', 'gold', 'platinum'];

const inr = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');
const pct = (r) => `${Math.round((Number(r) || 0) * 1000) / 10}%`;

/** A rate is either a fraction or a flat fee — render whichever it is. */
const rateLabel = (rate) => {
  if (rate === undefined || rate === null) return '—';
  if (typeof rate === 'object') return `${inr(rate.flat)} / client`;
  return pct(rate);
};

const AdminRateCard = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [cards, setCards] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [negotiated, setNegotiated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      axios.get(`${API_URL}/api/admin/negotiated-terms`, { headers: getAuthHeaders() })
        .then((r) => setNegotiated(r.data.partners || []))
        .catch(() => { /* non-blocking: the card still edits without the list */ });
      const res = await axios.get(`${API_URL}/api/admin/rates`, { headers: getAuthHeaders() });
      setCards(res.data.cards || []);
      setCurrentVersion(res.data.current_version);
    } catch (e) {
      toast.error('Could not load the rate card');
    } finally {
      setLoading(false);
    }
  };

  /** Start a new version from the current one — you are always editing a copy. */
  const openDialog = () => {
    const current = cards.find((c) => c.is_current) || cards[0];
    if (!current) return;
    setDraft(JSON.parse(JSON.stringify({
      commission: current.commission,
      tier_thresholds: current.tier_thresholds,
      wallet: current.wallet,
    })));
    setNote('');
    setDialogOpen(true);
  };

  const setCommission = (arrangement, tier, value) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      // Percentages are entered as 20 and stored as 0.20 — the API refuses anything above 1,
      // so the conversion has to happen here rather than being left to whoever is typing.
      next.commission[arrangement][tier] = Number(value) / 100;
      return next;
    });
  };

  const setThreshold = (tier, value) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next.tier_thresholds[tier] = Number(value);
      return next;
    });
  };

  const setWallet = (field, value) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next.wallet[field] = Number(value);
      return next;
    });
  };

  const setDiscount = (tier, value) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next.wallet.discount = { ...(next.wallet.discount || {}), [tier]: Number(value) / 100 };
      return next;
    });
  };

  const publish = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/rates`, { note, ...draft }, { headers: getAuthHeaders() });
      toast.success('New version published — it applies to partners who sign from now on');
      setDialogOpen(false);
      fetchCards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not publish');
    } finally {
      setSaving(false);
    }
  };

  const current = cards.find((c) => c.is_current);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-rate-card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
              <Receipt className="w-6 h-6" /> Rate card
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              Every commission rate, tier threshold and wallet discount. Changing one publishes a
              new version — partners already earning keep the card they signed under.
            </p>
          </div>
          <Button onClick={openDialog} disabled={!current} data-testid="new-version">
            <Plus className="w-4 h-4 mr-2" /> Publish a new version
          </Button>
        </div>

        {loading && <p className="text-sm text-[#64748B]">Loading…</p>}

        {current && (
          <>
            <Card>
              <CardContent className="pt-6">
                <h2 className="font-semibold mb-3">
                  Commission — version {current.version}
                </h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client arrangement</TableHead>
                      {TIERS.map((t) => <TableHead key={t} className="capitalize">{t}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ARRANGEMENTS.map((arrangement) => (
                      <TableRow key={arrangement}>
                        <TableCell className="capitalize font-medium">{arrangement}</TableCell>
                        {TIERS.map((tier) => (
                          <TableCell key={tier}>
                            {rateLabel(current.commission?.[arrangement]?.[tier])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="text-xs text-[#64748B] mt-3">
                  The row is how the <strong>client</strong> is engaged; the column is the
                  partner's tier. One partner can be earning three of these rates at once.
                  A reseller client earns no commission — that partner's margin is their
                  wallet discount.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <h2 className="font-semibold mb-3">How partners climb</h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tier</TableHead>
                      <TableHead>Reached at (₹ we earn / month)</TableHead>
                      <TableHead>Buys credits at</TableHead>
                      <TableHead>₹100,000 of credits costs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {TIERS.map((tier) => {
                      const discount = current.wallet?.discount?.[tier] || 0;
                      return (
                        <TableRow key={tier}>
                          <TableCell className="capitalize font-medium">{tier}</TableCell>
                          <TableCell>
                            {tier === 'silver'
                              ? 'everyone starts here'
                              : inr(current.tier_thresholds?.[tier])}
                          </TableCell>
                          <TableCell>{pct(discount)} off</TableCell>
                          <TableCell>{inr(100000 * (1 - discount))}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <p className="text-xs text-[#64748B] mt-3">
                  One ladder, for every partner. The figure is what <strong>we</strong> earn in a
                  month — the clients we bill, plus the cash they pay us for credits, added.
                  Not the commission we pay them.
                </p>
                <p className="text-xs text-[#64748B] mt-3">
                  Minimum purchase {inr(current.wallet?.floor)} · credits expire{' '}
                  {current.wallet?.expiry_months} months from purchase, and{' '}
                  {current.wallet?.client_expiry_months} months after they land in a client ·
                  every client is seeded with {current.wallet?.min_client_seed} credits and
                  must receive that much again each window.
                </p>
              </CardContent>
            </Card>
          </>
        )}

        {/* Partners who are NOT on this card.
            This exists because negotiated terms do not expire and nothing bounds them: an
            introductory rate stays until somebody sends a new proposal, and nobody re-papers
            what they cannot see. */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <h2 className="font-semibold">Partners on negotiated terms</h2>
              <span className="text-xs text-[#64748B]">
                {negotiated.filter((n) => n.live).length} live
              </span>
            </div>
            {negotiated.length === 0 ? (
              <p className="text-sm text-[#64748B]">
                Everyone is on the published card. Terms are changed per partner by editing the
                numbers on their proposal before sending it.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Partner</TableHead>
                      <TableHead>Card</TableHead>
                      <TableHead>What differs from standard</TableHead>
                      <TableHead>Agreed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {negotiated.map((n) => (
                      <TableRow key={n.version} className={n.live ? '' : 'opacity-50'}
                                data-testid={`negotiated-${n.version}`}>
                        <TableCell>
                          <span className="font-medium">{n.company_name || n.full_name || n.partner_id}</span>
                          {!n.live && <span className="ml-2 text-xs text-[#64748B]">(superseded)</span>}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-[#64748B]">
                          v{n.version} from v{n.derived_from}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {Object.entries(n.changed || {}).map(([path, d]) => (
                              <p key={path} className="text-xs">
                                <span className="font-mono text-[#64748B]">{path}</span>{' '}
                                <span className="text-[#64748B]">{String(d.from)} →</span>{' '}
                                <span className="font-semibold text-[#1A1A1A]">{String(d.to)}</span>
                              </p>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-[#64748B]">
                          {(n.created_at || '').slice(0, 10)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="font-semibold mb-3">Every version</h2>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Partners on it</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cards.map((c) => (
                  <TableRow key={c.version} data-testid={`card-v${c.version}`}>
                    <TableCell className="font-medium">
                      v{c.version}{' '}
                      {c.is_current && (
                        <span className="text-xs text-green-700 ml-1">current</span>
                      )}
                    </TableCell>
                    <TableCell>{(c.created_at || '').slice(0, 10)}</TableCell>
                    <TableCell className="text-sm text-[#64748B]">{c.created_by}</TableCell>
                    <TableCell className="text-sm">{c.note || '—'}</TableCell>
                    <TableCell>{c.partners_pinned}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-[#64748B] mt-3 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Published versions are never edited. To go back, publish
              a new version carrying the old numbers.
            </p>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Publish version {(currentVersion || 0) + 1}</DialogTitle>
            </DialogHeader>

            {draft && (
              <div className="space-y-5">
                <div>
                  <label className="text-sm font-medium">What changed, and why</label>
                  <Input value={note} onChange={(e) => setNote(e.target.value)}
                         placeholder="e.g. Gold reachable at fifty thousand a month" className="mt-1" />
                </div>

                <div className="border rounded-lg p-3">
                  <p className="font-medium mb-2">How partners climb — one ladder, for every partner</p>
                  <div className="grid grid-cols-2 gap-3">
                    {['gold', 'platinum'].map((tier) => (
                      <div key={tier}>
                        <label className="text-xs text-[#64748B] capitalize">
                          {tier} reached at (₹ we earn / month)
                        </label>
                        <Input
                          type="number"
                          value={draft.tier_thresholds?.[tier] ?? 0}
                          onChange={(e) => setThreshold(tier, e.target.value)}
                          data-testid={`threshold-${tier}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#64748B] mt-2">
                    What we earn, not what we pay out: the clients we bill plus the cash they
                    pay for credits. Silver is where everyone starts and needs no threshold.
                  </p>
                </div>

                {ARRANGEMENTS.map((arrangement) => (
                  <div key={arrangement} className="border rounded-lg p-3">
                    <p className="font-medium capitalize mb-2">
                      {arrangement} clients — commission by the partner's tier
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {TIERS.map((tier) => {
                        const value = draft.commission[arrangement][tier];
                        const isFlat = typeof value === 'object';
                        return (
                          <div key={tier}>
                            <label className="text-xs text-[#64748B] capitalize">
                              {tier} {isFlat ? '(flat ₹)' : '(%)'}
                            </label>
                            <Input
                              type="number" step="0.1" disabled={isFlat}
                              value={isFlat ? value.flat : Math.round(value * 1000) / 10}
                              onChange={(e) => setCommission(arrangement, tier, e.target.value)}
                              data-testid={`rate-${arrangement}-${tier}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="border rounded-lg p-3">
                  <p className="font-medium mb-2">Reseller — credits</p>
                  <p className="text-xs text-[#64748B] mb-3">
                    The reseller model. The minimum purchase is the gate; the discount ladder
                    below is the rate. A reseller client earns no commission — the margin is
                    taken here, at purchase.
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-[#64748B]">Minimum purchase (₹ face value)</label>
                      <Input type="number" value={draft.wallet.floor}
                             onChange={(e) => setWallet('floor', e.target.value)}
                             data-testid="wallet-floor" />
                    </div>
                    <div>
                      <label className="text-xs text-[#64748B]">Expire in the wallet (months)</label>
                      <Input type="number" value={draft.wallet.expiry_months}
                             onChange={(e) => setWallet('expiry_months', e.target.value)}
                             data-testid="wallet-expiry-months" />
                    </div>
                    <div>
                      <label className="text-xs text-[#64748B]">Expire inside a client (months)</label>
                      <Input type="number" value={draft.wallet.client_expiry_months}
                             onChange={(e) => setWallet('client_expiry_months', e.target.value)}
                             data-testid="client-expiry-months" />
                    </div>
                    <div>
                      <label className="text-xs text-[#64748B]">Client seed and refill (credits)</label>
                      <Input type="number" value={draft.wallet.min_client_seed}
                             onChange={(e) => setWallet('min_client_seed', e.target.value)}
                             data-testid="min-client-seed" />
                    </div>
                  </div>
                  <p className="text-xs text-[#64748B] mb-3">
                    A credit lives at most both windows added together — one quarter in the
                    wallet, one more inside the client. The seed is charged to the partner when
                    a client is onboarded, and the same amount must reach that client inside
                    every client window or it is suspended like any unpaid org.
                  </p>
                  <p className="font-medium mb-2 mt-4">Reseller rate — the discount they buy at</p>
                  <div className="grid grid-cols-3 gap-3">
                    {TIERS.map((tier) => (
                      <div key={tier}>
                        <label className="text-xs text-[#64748B] capitalize">{tier} (%)</label>
                        <Input
                          type="number" step="0.1"
                          value={Math.round((draft.wallet.discount?.[tier] || 0) * 1000) / 10}
                          onChange={(e) => setDiscount(tier, e.target.value)}
                          data-testid={`discount-${tier}`}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#64748B] mt-2">
                    Must increase with the tier — a bigger partner cannot buy at a worse rate
                    than a smaller one. Purchase size no longer changes the discount.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={publish} disabled={saving} data-testid="publish">
                {saving ? 'Publishing…' : 'Publish'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminRateCard;
